if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get productMediaWrapper() {
        return (this._productMediaWrapper = this._productMediaWrapper || this.querySelector('.product__media-wrapper'));
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        

        if (this.productMediaWrapper) {
          this.blockPositionHandler = this.handleBlocksPosition.bind(this);
          this.handleBlocksPosition();
          document.addEventListener('matchMobile', this.blockPositionHandler);
          document.addEventListener('unmatchMobile', this.blockPositionHandler);
        }
      }

      handleBlocksPosition() {
        const isMobile = FoxTheme.config.mqlMobile;
        const blocks = this.querySelectorAll(`.product__block[data-show-below-media="true"]`);

        blocks.forEach((block) => {
          const blockId = block.dataset.blockId;
          const blockPlacement = this.productMediaWrapper.querySelector(`[data-block-id="${blockId}"]`);
          if (!blockPlacement) return;

          if (isMobile) {
            const isMoveToMedia = block.hasAttribute('data-moved-below-media');
            if (isMoveToMedia) {
              block.innerHTML = blockPlacement.innerHTML;
              blockPlacement.innerHTML = '';
              block.removeAttribute('data-moved-below-media');
            }
          } else {
            blockPlacement.innerHTML = block.innerHTML;
            block.innerHTML = '';
            block.setAttribute('data-moved-below-media', '');
          }
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks,
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut,
            true,
            addButtonUpdated?.querySelector('.btn__text'),
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            }),
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      // updatedText: the server-rendered `.btn__text` for the new variant. When
      // given, it replaces the label as-is so the price and layout classes follow.
      toggleAddButton(disable = true, text, modifyClass = true, updatedText = null) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (updatedText) {
          addButton.toggleAttribute('disabled', disable);
          addButtonText.className = updatedText.className;
          addButtonText.innerHTML = updatedText.innerHTML;
        } else if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    },
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    },
  );
}

// Price inside the main add-to-cart button: the Kaching Bundles total once a
// bundle is selected, otherwise quantity × unit price, where the unit price is
// the pickup price when pickup pricing is active, else the variant price. A
// ticked <product-discount-code> then takes its discount off that total. The
// element is re-rendered from server HTML on every variant change (see
// toggleAddButton).
if (!customElements.get('atc-price')) {
  // Latest Kaching `variants-changed` detail per product-info. Kept outside the
  // element because the element is replaced on variant change.
  const kachingDetails = new WeakMap();
  const scopeOf = (el) => el.closest('product-info') || document.documentElement;

  // Kaching dispatches on its block without bubbling, so listen in capture.
  document.addEventListener(
    'variants-changed',
    (event) => {
      const block = event.target instanceof Element && event.target.closest('kaching-bundles-block');
      if (!block || !event.detail?.pricing) return;
      const scope = scopeOf(block);
      kachingDetails.set(scope, event.detail);
      scope.querySelectorAll('atc-price').forEach((price) => price.render());
    },
    true,
  );

  // Every button re-checks: the sticky ATC bar sits outside product-info.
  document.addEventListener('fulfillment:pricing-change', () => {
    document.querySelectorAll('atc-price').forEach((price) => price.render());
  });

  document.addEventListener('discount-code:change', () => {
    document.querySelectorAll('atc-price').forEach((price) => price.render());
  });

  customElements.define(
    'atc-price',
    class AtcPrice extends HTMLElement {
      connectedCallback() {
        // Kaching rewrites any price-looking text inside the add-to-cart button
        // with its own value (and again 2s later). It only walks light DOM, so the
        // amount lives in a shadow root; the server-rendered text is the no-JS
        // fallback.
        if (!this.shadowRoot) {
          this.attachShadow({ mode: 'open' });
          this.replaceChildren();
        }

        this.abortController = new AbortController();
        const onQuantityChange = (event) => {
          if (event.target === this.quantityInput) this.render();
        };
        document.addEventListener('input', onQuantityChange, { signal: this.abortController.signal });
        document.addEventListener('change', onQuantityChange, { signal: this.abortController.signal });

        this.render();
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      // The quantity this button's own form submits. Looked up through the form:
      // the sticky ATC bar's input reuses the same `Quantity-<section>` id.
      get quantityInput() {
        const field = this.closest('form')?.elements.namedItem('quantity');
        return field instanceof HTMLInputElement ? field : field?.[0] || null;
      }

      get quantity() {
        const quantity = parseInt(this.quantityInput?.value, 10);
        return quantity > 0 ? quantity : 1;
      }

      // Kaching's total, only while a bundle is selected for the current variant.
      // With no bundle Kaching still reports the plain variant price, which knows
      // nothing about pickup pricing, so that case is left to the checks below.
      // Also ignored until Kaching reports on this variant, since it re-emits a
      // moment after a variant switch.
      // Kaching's discountedPrice ({ amount, formatted }) while a bundle is selected.
      get bundlePrice() {
        // data-exclude-bundle: buttons whose form adds only the variant (sticky
        // ATC bar), never the Kaching bundle.
        if (this.hasAttribute('data-exclude-bundle')) return null;
        const detail = kachingDetails.get(scopeOf(this));
        const items = detail?.variantIdQuantities || [];
        if (!items.some(({ variantId }) => String(variantId) === this.dataset.variantId)) return null;

        // pricing.fullPrice is the compare-at price, so a bundle discount is
        // detected against the variant's own price instead.
        const { discountedPrice } = detail.pricing;
        const quantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const isBundle =
          quantity > 1 ||
          detail.bundleProducts?.length > 0 ||
          detail.freeGifts?.length > 0 ||
          detail.upsells?.length > 0 ||
          discountedPrice?.amount !== Number(this.dataset.price);
        return isBundle && discountedPrice?.formatted ? discountedPrice : null;
      }

      // Set by the fulfillment selector while pickup is chosen and priced.
      get pickupPrice() {
        const { sectionId } = this.dataset;
        const selector = sectionId
          ? document.querySelector(`fulfillment-selector[data-section-id="${sectionId}"][data-pickup-price]`)
          : scopeOf(this).querySelector('fulfillment-selector[data-pickup-price]');
        if (!selector || selector.dataset.pickupVariantId !== this.dataset.variantId) return null;
        return Number(selector.dataset.pickupPrice);
      }

      // The ticked discount code block for this button's product, if any.
      get discountCode() {
        const selector = 'product-discount-code[data-applied]';
        const { sectionId } = this.dataset;
        return sectionId
          ? document.querySelector(`${selector}[data-section-id="${sectionId}"]`)
          : scopeOf(this).querySelector(selector);
      }

      // For buttons that switch variants client-side (sticky ATC bar) instead of
      // being re-rendered from server HTML.
      setVariant(variant) {
        if (!variant) return;
        this.dataset.variantId = variant.id;
        this.dataset.price = variant.price;
        this.render();
      }

      render() {
        const bundle = this.bundlePrice;
        const discountCode = this.discountCode;
        let price;
        if (bundle && !discountCode) {
          price = bundle.formatted;
        } else {
          const total = bundle ? bundle.amount : (this.pickupPrice ?? Number(this.dataset.price)) * this.quantity;
          const amount = discountCode ? discountCode.applyDiscount(total) : total;
          price = FoxTheme.Currency.formatMoney(amount, FoxTheme.settings.moneyFormat);
        }
        if (this.shadowRoot.innerHTML !== price) this.shadowRoot.innerHTML = price;
      }
    },
  );
}
